import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Table from "@/common/components/Table";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import { DataWrapper } from "@/common/components/dataWrapper";
import { Modal } from "@/common/components/modal";
import { Prompt } from "@/common/components/prompt";
import { ActionPopup } from "@/common/components/action-popup";
import { usePositioning } from "@/hooks/usePositioning";
import { PRODUCT_URL, INVENTORY_URL } from "@/common/constants";
import { KeyValueObj } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import useQuery from "@/hooks/useQuery";
import { batchTableFilterOptions, batchTableHeaders, batchTableKeys } from "./subs/data";
import { BatchSerialModal } from "./subs/BatchSerialModal";
import { EditBatchModal } from "./subs/EditBatchModal";
import { BatchDiscountModal } from "./subs/BatchDiscountModal";
import { formatToNaira } from "@/common/utils/general";
import { QuantityCell } from "@/common/components/quantity-cell";
import { ProductInventoryViewWrapper } from "./subs/style";

export interface IProductInventoryViewProps {}

// MED-659: what a buyer actually pays for this batch — the listing price with
// any clearance discount applied. Reads "Not set" rather than ₦0 when the
// product has no price, matching the inventory list (UC-INV-02).
function buildBatchPriceLabel(batch: KeyValueObj): string {
  const discount = batch?.discount;
  const effective = discount?.effective_price_per_unit;
  if (effective === null || effective === undefined) return "Not set";

  const price = formatToNaira(Number(Number(effective).toFixed(2)));
  return discount?.discounted ? `${price} (−${discount.discount_percentage}%)` : price;
}

// MED-652 stage 2: a single product's batch list, drilled into from the
// product-aggregated inventory page (add-inventory/index.tsx). Each batch
// row's own click opens the stage-3 serial-tree modal (view); Edit/Delete
// are the row actions relocated from the old flat per-batch table.
export function ProductInventoryView(_: IProductInventoryViewProps) {
  const { productId } = useParams();
  const apiCall = useApiCallHandling();

  const {
    data: product,
    loading: productLoading,
    error: productError,
    refetch: refetchProduct,
  } = useQuery(`${PRODUCT_URL}/${productId}`);
  const {
    data: batches,
    loading: batchesLoading,
    error: batchesError,
    refetch: refetchBatches,
  } = useQuery<KeyValueObj[]>(`${PRODUCT_URL}/${productId}/batches`);

  const [selectedBatch, setSelectedBatch] = useState<KeyValueObj | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState<KeyValueObj | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // MED-659 (UC-LST-02): the clearance discount on a single batch.
  const [discountingBatch, setDiscountingBatch] = useState<KeyValueObj | null>(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState("");
  const [batchPendingDelete, setBatchPendingDelete] = useState<KeyValueObj | null>(null);
  // Row actions menu (Edit/Delete/View) — the app's conventional three-dot
  // pattern, matching discounts/orders/cluster (Table "dots" button +
  // ActionPopup positioned via usePositioning).
  const [setActionPosition, actionPosition] = usePositioning();
  const [showRowActions, setShowRowActions] = useState(false);
  const [actionBatchId, setActionBatchId] = useState<string | null>(null);

  // Search + filter — the /batches endpoint returns the full list for this
  // product with no query params, so both are applied client-side.
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValues, setFilterValues] = useState<KeyValueObj>({});

  function openBatchModal(id: string | number) {
    const batch = (batches || []).find((b) => b.id === id);
    if (!batch) return;
    setSelectedBatch(batch);
    setShowBatchModal(true);
  }

  function openEditModal(id: string) {
    const batch = (batches || []).find((b) => b.id === id);
    if (!batch) return;
    setEditingBatch(batch);
    setShowEditModal(true);
  }

  function openDiscountModal(id: string) {
    const batch = (batches || []).find((b) => b.id === id);
    if (!batch) return;
    setDiscountingBatch(batch);
  }

  function onBatchUpdated() {
    refetchBatches();
  }

  function requestDeleteBatch(id: string) {
    const batch = (batches || []).find((b) => b.id === id);
    if (!batch) return;
    setBatchPendingDelete(batch);
  }

  function handleRowActionsClick(e: any, rowId: string) {
    setActionPosition(e);
    setActionBatchId(rowId);
    setShowRowActions(true);
  }

  function closeRowActions() {
    setShowRowActions(false);
    setActionBatchId(null);
  }

  function handleViewAction() {
    if (actionBatchId) openBatchModal(actionBatchId);
    closeRowActions();
  }

  function handleEditAction() {
    if (actionBatchId) openEditModal(actionBatchId);
    closeRowActions();
  }

  // MED-659 (UC-LST-02): the clearance discount is a row action like the rest.
  // Its label names the operation on the batch the menu was opened over, so a
  // batch already discounted reads "Edit discount" rather than offering to add
  // a second one.
  function handleDiscountAction() {
    if (actionBatchId) openDiscountModal(actionBatchId);
    closeRowActions();
  }

  function handleDeleteAction() {
    if (actionBatchId) requestDeleteBatch(actionBatchId);
    closeRowActions();
  }

  function confirmDeleteBatch() {
    if (!batchPendingDelete) return;
    const id = batchPendingDelete.id;
    setDeleteLoadingId(id);
    apiCall(
      `${INVENTORY_URL}/${id}`,
      "DELETE",
      {},
      () => refetchBatches(),
      () => {},
      () => {
        setDeleteLoadingId("");
        setBatchPendingDelete(null);
      },
    );
  }

  const filteredBatches = useMemo(() => {
    let result = batches || [];

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((batch) =>
        String(batch.batch_number ?? "").toLowerCase().includes(term),
      );
    }

    if (filterValues.manufacture_date) {
      result = result.filter(
        (batch) => batch.manufacture_date?.slice(0, 10) === filterValues.manufacture_date,
      );
    }
    if (filterValues.expiry_date) {
      result = result.filter(
        (batch) => batch.expiry_date?.slice(0, 10) === filterValues.expiry_date,
      );
    }

    return result;
  }, [batches, searchTerm, filterValues]);

  function handleSearch(value: string) {
    setSearchTerm(value);
  }

  function handleFilter(values: KeyValueObj) {
    setFilterValues(values);
  }

  const tableData = useMemo(
    () =>
      filteredBatches.map((batch) => ({
        batch_number: { value: batch.batch_number, type: "string" },
        manufacture_date: { value: batch.manufacture_date, type: "string" },
        expiry_date: { value: batch.expiry_date, type: "string" },
        // MED-697: "1 Carton", not the stored "10", counted at the level this
        // owner transacts at. The base-unit figure is still what a dispatch or an
        // order is checked against; it is just not what a person should be asked
        // to read off a shelf. Anything left below the selling level is one click
        // away rather than crowding the column.
        quantity_in_stock: {
          value: (
            <QuantityCell
              display={batch.quantity_display?.in_stock}
              fallbackBaseUnits={batch.quantity_in_stock}
            />
          ),
          type: "string",
        },
        // MED-659: the effective price for this batch — the listing price with
        // any clearance discount applied. Both figures come from the server;
        // nothing is computed here.
        price: { value: buildBatchPriceLabel(batch), type: "string" },
        action: {
          type: "button",
          buttonType: "dots",
          buttonLoading: deleteLoadingId === batch.id,
          onClick: handleRowActionsClick,
        },
        id: batch.id,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredBatches, deleteLoadingId],
  );

  return (
    <ProductInventoryViewWrapper>
      <PageTitleNavs
        title="Product Inventory"
        backLink="/dashboard/inventories"
        bcrumbs={[
          { title: "Inventories", link: "/dashboard/inventories" },
          { title: product?.brand_name || "Product", link: "#" },
        ]}
      />

      <DataWrapper
        data={product}
        noDataMessage="Product not found"
        loading={productLoading}
        error={productError}
        loadingMessage="Loading product details..."
        failedMessage="Could not fetch product details."
        // MED-675: Retry was rendered but wired to nothing — onFailAction
        // was never passed, so DataWrapper's onRetry?.() was a no-op and a
        // failed fetch was permanently stuck (useQuery only re-runs its
        // effect on url/enabled change, neither of which retry affects).
        onFailAction={refetchProduct}
      >
        <div className="product-header">
          <span className="brand">{product?.brand_name}</span>
          <span className="meta">
            SKU: {product?.sku} · {product?.form} {product?.strength}
            {product?.strength_unit}
          </span>
        </div>
      </DataWrapper>

      {/* MED-681: editing an approved product resets its approval_status to
          "pending" and re-queues it for admin review — the product is
          unsellable in that state, but the batches below are unaffected and
          stay fully visible, so this reads before the stock figures rather
          than replacing them. Clears itself: it's driven straight off
          product.approval_status, which flips on the next fetch once the
          admin approves/declines/queries the edit — no seller action or
          local dismiss state involved. */}
      {product?.approval_status === "pending" && (
        <div className="approval-pending-banner">
          This product has been edited and cannot be sold until an admin
          approves the change.
        </div>
      )}

      <div className="table-wrapper">
        {/* MED-677: no `clickable` — the row's own three-dot menu (View/
            Edit/Delete below) is the single, consistent action entry
            point; the separate navigation-arrow column read as "go
            somewhere" rather than "act on this row". */}
        <Table
          tableHeaders={batchTableHeaders}
          tableData={tableData}
          tableKeys={batchTableKeys}
          full
          rowLineColor="light"
          loading={batchesLoading}
          errorLoading={!!batchesError}
          onTRClick={openBatchModal}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          filterInputs={batchTableFilterOptions}
        />
      </div>

      <ActionPopup
        position={actionPosition}
        open={showRowActions}
        onClose={closeRowActions}
        actions={[
          { label: "View", action: handleViewAction },
          { label: "Edit", action: handleEditAction },
          {
            label: (batches || []).find((b) => b.id === actionBatchId)?.discount?.discounted
              ? "Edit discount"
              : "Discount",
            action: handleDiscountAction,
          },
          { label: "Delete", action: handleDeleteAction },
        ]}
      />

      <BatchSerialModal
        isOpen={showBatchModal}
        close={() => setShowBatchModal(false)}
        batch={selectedBatch}
      />
      <EditBatchModal
        isOpen={showEditModal}
        close={() => setShowEditModal(false)}
        batch={editingBatch}
        onUpdated={onBatchUpdated}
      />

      {/* MED-659 (UC-LST-02). Mounted only while open so it always opens against
          the batch's current discount rather than a stale one. */}
      {discountingBatch && (
        <BatchDiscountModal
          isOpen
          close={() => setDiscountingBatch(null)}
          batch={discountingBatch}
          onUpdated={onBatchUpdated}
        />
      )}

      {/* MED-665: deletion must be gated behind an explicit confirm/cancel —
          the previous behavior deleted the batch immediately on click. */}
      <Modal
        title="Delete Batch"
        isOpen={!!batchPendingDelete}
        close={() => setBatchPendingDelete(null)}
      >
        <Prompt
          type="delete"
          prompt={`Are you sure you want to delete batch "${batchPendingDelete?.batch_number}"? This will permanently remove its inventory and cannot be undone.`}
          onMainAction={confirmDeleteBatch}
          onSecondaryAction={() => setBatchPendingDelete(null)}
          mainActionText="Delete"
          mainLoading={deleteLoadingId === batchPendingDelete?.id}
        />
      </Modal>
    </ProductInventoryViewWrapper>
  );
}
